import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NumberUtils, StringUtils } from '@pk/powerkioskutils';

import { custom } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment-timezone';
import { ToastrService } from 'ngx-toastr';

import { bidAmountValidator } from 'src/app/shared/validators/bid-amount-validator';
import { GraphqlService } from '../../graphql/graphql.service';
import { HelperService } from '../../shared/helper.service';
import { PricingComponent, Product, RfqSession, RfqSessionBid, RfqSessionProduct, RfqSessionStat, RfqSessionSupplier } from '../../shared/models';
import { AbstractPageForm } from '../../shared/pages/page-form.abstract';

@Component({
	selector: 'pk-broker-auction-supplier-create-bids',
	templateUrl: './auction-supplier-create-bids.component.html',
	styleUrls: ['./auction-supplier-create-bids.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AuctionSupplierCreateBidsComponent extends AbstractPageForm implements OnDestroy {
	@Input() rfqSession: RfqSession;
	@Input() rfqSessionSupplierId: string;
	@Input() products: Product[];

	@Output() bidsSubmitted: EventEmitter<boolean> = new EventEmitter<boolean>();

	private isRefreshing = false;
	public terms: number[];
	public bidMeasureUnits: { text: string; multiplier: number }[];
	public rfqSessionSupplier: RfqSessionSupplier;
	public showIsGRTIncluded: boolean;
	public pricingComponents: PricingComponent[];

	public greenPercentages: number[];
	public bandwidthPercentages: number[];
	public billingMethods: { value: string; text: string }[];

	public interval: NodeJS.Timeout;

	constructor(
		private toastrService: ToastrService,
		private decimalPipe: DecimalPipe,
		private graphqlService: GraphqlService,
		private helperService: HelperService,
		private fb: FormBuilder,
	) {
		super();
		this.submitText = 'Submit Bids';
		this.submitAudit = 'auction-supplier-create-bids:submitBids';
	}

	public bandwidthPercentageTextMap = (option: number) => `${option}%`;
	public yesNoTextMap = (option: boolean) => (option ? 'Yes' : 'No');
	public termTextMap = (option: number) => `${option} month${option === 1 ? '' : 's'}`;
	public greenPercentageTextMap = (option: number) => `${option}%`;

	get isAuctionComplete() {
		const isLate = moment(this.rfqSession.endDate2) < moment();
		return this.rfqSession.contract.status === this.CONSTANTS.statuses.acomp || isLate;
	}

	get productBidErrors() {
		return this.err.productBids?.some(b => b.group);
	}

	public async loadPageData(): Promise<void> {
		this.bandwidthPercentages = this.helperService.generateNumberList(100, 0, 5);
		this.billingMethods = [
			{ value: 'Single Bill from Utility', text: 'Single Bill from Utility' },
			{ value: 'Single Bill from Supplier', text: 'Single Bill from Supplier' },
			{ value: 'Two Bills', text: 'Two Bills' },
		];
		this.terms = [...this.helperService.generateNumberList(1, 59), ...this.helperService.generateNumberList(60, 120, 12)];
		this.greenPercentages = this.helperService.generateNumberList(0, 100, 10);
		this.bidMeasureUnits = this.CONSTANTS.units[this.rfqSession.contract.serviceTypeId];
		this.showIsGRTIncluded = this.rfqSession.contract.showGRTIncluded();
		this.rfqSessionSupplier = this.rfqSession.suppliers.find(s => s.rfqSessionSupplierId === this.rfqSessionSupplierId);
		this.rfqSession.products.forEach(p => {
			if (p.bestBids) {
				p.supplierBestBid = _.orderBy(
					p.bestBids.filter(b => b.rfqSessionSupplierId === this.rfqSessionSupplierId),
					'rank',
				)[0];
			}
		});
		const products = this.products.filter(p => this.rfqSession.products.some(rp => rp.productId === p.productId));
		this.pricingComponents = _.sortBy(
			_.uniqBy(
				_.flatMap(products, p => p.pricingComponents),
				'id',
			),
			'name',
		);
	}

	public getForm() {
		const bestBid = this.rfqSession.products?.[0].supplierBestBid ?? null;
		const units = bestBid?.units ? this.bidMeasureUnits.find(u => u.text === bestBid.units) : this.bidMeasureUnits[0];
		return this.fb.group({
			bandwidthPercentage: [bestBid?.bandwidthPercentage ?? this.bandwidthPercentages[0], [Validators.required]],
			billingMethod: [
				bestBid?.billingMethod
					? this.billingMethods.find(b => b.value.toLowerCase() === bestBid.billingMethod.toLowerCase()).value
					: this.billingMethods[0].value,
				[Validators.required],
			],
			salesTax: [bestBid?.salesTax ?? 0, [Validators.required]],
			isGRTIncluded: this.fb.control(
				bestBid ? StringUtils.toBoolean(bestBid.isGRTIncluded) : null,
				this.showIsGRTIncluded ? [Validators.required] : [],
			),
			units: this.fb.control(units),
			productBids: this.fb.array(this.rfqSession.products?.map(p => this.getProductBidGroup(p, units)) ?? []),
		});
	}

	private productHasPricingComponents(productId: string, pricingComponentId: number) {
		return this.products.find(p => p.productId === productId)?.pricingComponents.some(p => p.id === pricingComponentId);
	}

	private getProductBidGroup(product: RfqSessionProduct, units: this['bidMeasureUnits'][0], bidAmount: number = null) {
		return this.fb.group({
			bidAmount,
			entity: product,
			units: this.fb.control(units),
			serviceTypeId: this.rfqSession.contract.serviceTypeId,
			stateId: this.rfqSession.contract.stateId,
			// Add pricing components as a form array
			rfqSessionBidPricingComponents: this.fb.array(
				this.pricingComponents.map(pc =>
					this.fb.group({
						pricingComponentId: pc.id,
						amount: this.fb.control({
							value: null,
							disabled: !this.productHasPricingComponents(product.productId, pc.id),
						}),
					}),
				),
			),
		});
	}

	private addProductBids(bidAmount: number, product: RfqSessionProduct): void {
		this.form.productBids.control.push(this.getProductBidGroup(product, this.form.units.value, bidAmount));
	}

	public async onFormLoaded(): Promise<void> {
		this.form.units.valueChanges.subscribe(units => {
			this.updateBidMeasureUnits(units);
		});

		// get the best bid every 30 seconds
		this.interval = setInterval(() => {
			this.refreshBids();
		}, 30000);
	}

	ngOnDestroy(): void {
		clearInterval(this.interval);
	}

	protected async onFormSubmitted() {
		const bidAmountValidationResult = await this.getBidAmountValidationResult();

		if (!bidAmountValidationResult) {
			return;
		}

		const bidConfirmationResult = await this.getBidConfirmationResult();

		if (bidConfirmationResult) {
			const newProducts = [];
			const productResults = [];
			// add the bidAmounts to the products now, and then submit the bids
			productResults.forEach((productResult, index) => {
				if (productResult.data.createRfqSessionProduct.id) {
					this.addProductBids(newProducts[index], productResult.data.createRfqSessionProduct);
				}
			});

			const productBids: Subset<RfqSessionBid[]> = [];
			// send all bids in one API request
			for (const productBid of this.form.productBids.filter(p => p.bidAmount.value)) {
				productBids.push(this.createProductBidDTO(productBid));
			}

			await this.graphqlService.createRfqSessionBids(productBids);

			if (!this.warnings.length) {
				this.refreshBids(true);
			}
		}
	}

	private async getBidAmountValidationResult(): Promise<boolean> {
		const products = [];

		this.form.productBids.forEach(p => {
			if (bidAmountValidator(p.control)) {
				const amount = `${this.decimalPipe.transform(p.bidAmount.value, '1.6-6')} ` + `${this.form.units.value.text}`;

				products.push(`<li>${this.getProductBidText(p)}: ${amount}</li>`);
			}
		});

		if (!products.length) {
			return true;
		}

		const message = `
		The bid amount you entered for the following product(s) is outside the usual limits. Please verify that this is the amount you intend to submit.
		<ul>
			${products.join(' ')}
		</ul>`;
		const customDialog = custom({
			title: 'Bid Confirmation',
			messageHtml: message,
			buttons: [
				{
					text: 'Verify',
					onClick: () => true,
				},
				{
					text: 'Cancel',
					onClick: () => false,
				},
			],
		});

		return customDialog.show();
	}

	private async getBidConfirmationResult(): Promise<boolean> {
		const symbol = this.rfqSession.contract.state.country.symbol;
		const marginText = this.rfqSession.contract.getTotalMargin(true, true, false, this.rfqSession.contract.units);
		const margin = `${this.decimalPipe.transform(marginText, '1.6-6')} ` + `${symbol}/${this.rfqSession.contract.units}`;
		let subTitle =
			this.rfqSession.contract.stateId === this.CONSTANTS.states.newJersey ? '<li>Please confirm that you included SUT into your bid(s).</li>' : '';
		subTitle += '<li>Please confirm the margin included in your bid(s).</li>';
		const message = `
		<ul>
			${subTitle}
		</ul>
		<div class="row">
			<div class="col-md-12">
				<div class="form-element-container">
					<label class="title">Margin</label>
					<input type="text" class="form-control" id="margin" value="${margin}" readonly>
				</div>
			</div>
		</div>`;
		const customDialog = custom({
			title: 'Bid Confirmation',
			messageHtml: message,
			buttons: [
				{
					text: this.rfqSession.contract.stateId === this.CONSTANTS.states.newJersey ? 'Yes to all' : 'Yes',
					onClick: () => true,
				},
				{
					text: 'Go Back',
					onClick: () => false,
				},
			],
		});

		return customDialog.show();
	}

	private filterBidPricingComponents(dto: any) {
		const rfqSessionProduct = this.rfqSession.products.find(p => p.rfqSessionProductId === dto.rfqSessionProductId);
		const product = this.products.find(p => p.productId === rfqSessionProduct.productId);
		dto.rfqSessionBidPricingComponents = dto.rfqSessionBidPricingComponents.filter(p =>
			product.pricingComponents.some(pc => pc.id === p.pricingComponentId),
		);
	}

	private createProductBidDTO(productBid: this['form']['productBids'][0]) {
		const dto = {
			rfqSessionSupplierId: this.rfqSessionSupplierId,
			bandwidthPercentage: Number(this.form.bandwidthPercentage.value),
			salesTax: Number(this.form.salesTax.value),
			billingMethod: this.form.billingMethod.value,
			rfqSessionId: this.rfqSession.rfqSessionId,
			amount: NumberUtils.rateToTherms(Number(productBid.bidAmount.value), this.form.units.value.text.split('/')[1]),
			rfqSessionProductId: productBid.entity.value.rfqSessionProductId,
			isGRTIncluded: this.showIsGRTIncluded ? StringUtils.toBoolean(this.form.isGRTIncluded.value) : false,
			units: this.form.units.value.text,
			rfqSessionBidPricingComponents: productBid.rfqSessionBidPricingComponents
				.filter(pc => !_.isNil(pc.amount.value))
				.map(pc => ({
					pricingComponentId: pc.pricingComponentId.value,
					amount: NumberUtils.rateToTherms(Number(pc.amount.value), this.form.units.value.text.split('/')[1]),
					units: this.form.units.value.text,
				})),
		};
		this.filterBidPricingComponents(dto);
		return dto;
	}

	public getProductBidText(productBid: this['form']['productBids'][0]) {
		const entity = productBid.entity.value;

		return `${entity.product.name} - ${entity.term} month${entity.term === 1 ? '' : 's'} - Green ${entity.greenPercentage}%`;
	}

	public generateErrorMessage(productId: string): string {
		const minBid = this.getProductMinBidAmount(productId);
		const maxBid = this.getProductMaxBidAmount(productId);

		return `Bid Amount must be between ${minBid} and ${maxBid}. Please enter bids in unit ${this.form.units.value.text}.`;
	}

	public getProductMinBidAmount(productId: string): string {
		return this.helperService.getProductMinBidAmount(productId, this.rfqSession.contract.serviceTypeId, this.form.units.value.text);
	}

	public getProductMaxBidAmount(productId: string): string {
		return this.helperService.getProductMaxBidAmount(
			productId,
			this.rfqSession.contract.serviceTypeId,
			this.rfqSession.contract.stateId,
			this.form.units.value.text,
		);
	}

	private async refreshBids(isSubmitting = false) {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			try {
				const result = await this.graphqlService.getAuctionInProgressBestBids(this.rfqSession.rfqSessionId);
				const bestBids = result.data.rfqSession.bestBids.map(b => new RfqSessionStat(b));
				for (const p of this.form.productBids) {
					if (result.data.rfqSession.bids?.length) {
						p.entity.value.bids = result.data.rfqSession.bids.filter(b => b.rfqSessionProductId === p.entity.value.rfqSessionProductId);
					}
					if (bestBids) {
						p.entity.value.supplierBestBid = _.chain(bestBids)
							.filter(b => b.rfqSessionProductId === p.entity.value.rfqSessionProductId && b.rfqSessionSupplierId === this.rfqSessionSupplierId)
							.orderBy(b => b.rank)
							.first()
							.value();
					}
				}
				this.isRefreshing = false;
				if (isSubmitting) {
					this.toastrService.success('Successfully Submitted Bids', 'Pricing Session');
					this.form.productBids.forEach(p => {
						p.bidAmount.setValue(null);
						p.rfqSessionBidPricingComponents.forEach(pc => pc.amount.setValue(null));
					});
					this.bidsSubmitted.emit(true);
				}
			} catch (e) {
				this.isRefreshing = false;
			}
		}
	}

	public updateBidMeasureUnits(units: { text: string; multiplier: number }): void {
		this.form.productBids.forEach(p => p.units.setValue(units));
	}
}
